import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import { datadogRum } from '@datadog/browser-rum';
import {
  disableAntNotificationsCss,
  disableMainHtmlTag,
  renderSkeletonHtmlString,
} from '../../utils/globalConsoleDisplayOverrides';
import { shipiumApplicationUrlHelper } from '../../utils/shipiumApplicationUrlHelper';
import { PARTNER_VIEW_HEADER_KEY } from '~/hooks/SessionContext';
import getGatewayUri from './getGatewayUri';
import { AUTH_PATHS, PACK_REDIRECT_PATHS } from '../../config';

type RetryableAxiosRequestConfig = AxiosRequestConfig & { _retry?: boolean };

// Lazy import to avoid circular dependencies
const getStore = () => import('~/store').then((m) => m.store);

// CancelToken deprecated in axios 0.22
// will need to move to signal api
// https://axios-http.com/docs/cancellation
const CancelToken = axios.CancelToken;
const source = CancelToken.source();

const gatewayUri = getGatewayUri();
const client = axios.create({
  baseURL: gatewayUri,
  withCredentials: true,
  cancelToken: source.token,
});

client.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<{ message: string }>) => {
    const supportUrl = 'https://docs.shipium.com/docs/contacting-shipium-support';
    const isRuleSetRequest = error.config?.url?.includes('/ruleSets') || error.config?.url?.includes('/rules');

    // Handle 413 Payload Too Large error with a more informative message
    // Also handle network errors on large payloads — in production, nginx returns 413
    // without CORS headers, so the browser blocks the response and Axios only sees "Network Error"
    const is413 = error?.response?.status === 413;
    const isLikelyPayloadTooLarge =
      !error.response && error.message === 'Network Error' && error.config?.data && isRuleSetRequest;

    if (is413 || isLikelyPayloadTooLarge) {
      error.message = isRuleSetRequest
        ? 'The request payload is too large. Carrier Selection Rules are designed for exception-based ' +
          'scenarios rather than large data sets (e.g., extensive postal code lists). ' +
          `Please contact support to discuss alternative approaches for your routing requirements: ${supportUrl}`
        : 'The request payload is too large. Please reduce the amount of data and try again, ' +
          `or contact support for assistance: ${supportUrl}`;

      return Promise.reject(error);
    }

    if (error?.response?.data?.message) {
      error.message = error.response.data.message;
    }
    return Promise.reject(error);
  },
);

// 401 Unauthorized interceptor - refresh token and retry failed requests
let isRefreshing = false;
let failedQueue: Array<{
  resolve: () => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
};

client.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableAxiosRequestConfig | undefined;

    // Skip if not 401, no config, or already retried
    if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Skip refresh/auth endpoints to avoid loops
    // Normalize URL to ensure leading slash for consistent path matching
    const url = originalRequest.url || '';
    const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
    if (AUTH_PATHS.some((path) => normalizedUrl.startsWith(path))) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue this request while refresh is in progress
      return new Promise<void>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => client(originalRequest));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const store = await getStore();
      const result = await store.getActions().session.refreshSessionSilent();

      if (result === undefined) {
        // Refresh failed - logout and reject all queued requests
        processQueue(error);
        store.getActions().session.logoutSso();
        return Promise.reject(error);
      }

      processQueue();
      return client(originalRequest); // Retry original request
    } catch (refreshError) {
      processQueue(refreshError);
      const store = await getStore();
      store.getActions().session.logoutSso();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

client.interceptors.response.use((response: AxiosResponse) => {
  const url = response.config.url || '';
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;

  if (PACK_REDIRECT_PATHS.some((path) => normalizedUrl.startsWith(path))) {
    // abort all requests and redirect to Pack App
    if (['site_manager', 'site_associate'].includes(response?.data?.user?.sessionPartnerRole)) {
      source.cancel('Redirecting to Pack application.');
      disableMainHtmlTag();
      disableAntNotificationsCss();
      // Show skeleton loading state
      renderSkeletonHtmlString();

      window.location.href = shipiumApplicationUrlHelper(window.location.hostname, 'pack');
    }
  }
  return response;
});

client.interceptors.request.use((config) => {
  if (localStorage.getItem(PARTNER_VIEW_HEADER_KEY) !== null) {
    config.headers[PARTNER_VIEW_HEADER_KEY] = 1;
  }

  return config;
});

// Capture region from backend for Datadog RUM
let regionCaptured = false;
client.interceptors.response.use((response: AxiosResponse) => {
  if (!regionCaptured && datadogRum.getInitConfiguration()) {
    const region = response.headers['x-region'];
    if (region) {
      datadogRum.setGlobalContextProperty('server_region', region);
      regionCaptured = true;
    }
  }
  return response;
});

export default client;
